import type { LocationRewrite } from '@tanstack/react-router';

/**
 * Path suffixes rendered as overlays from any deeper support-panel organizations URL.
 * Matched routes stay on the base path via rewrite; the browser keeps the full public URL.
 */
export const SUPPORT_PANEL_OVERLAY_SUFFIXES = [
  '/m/add-organization',
] as const;

export type SupportPanelOverlaySuffix =
  (typeof SUPPORT_PANEL_OVERLAY_SUFFIXES)[number];

/** Internal search key — stripped again on output so it never appears publicly. */
export const SUPPORT_PANEL_OVERLAY_SEARCH_PARAM = '__supportPanelOverlay';

const matchOverlaySuffix = (
  pathname: string,
): SupportPanelOverlaySuffix | undefined => SUPPORT_PANEL_OVERLAY_SUFFIXES.find(
  suffix => pathname.endsWith(suffix),
);

export function parseSupportPanelOverlay(
  pathname: string,
): SupportPanelOverlaySuffix | null {
  return matchOverlaySuffix(pathname) ?? null;
}

export function isSupportPanelOverlayPath(pathname: string): boolean {
  return matchOverlaySuffix(pathname) !== undefined;
}

/**
 * Browser URL keeps `/…/m/add-organization`; the router matches the base path so
 * organizations chrome stays mounted under the overlay.
 *
 * Open overlays with `routerService.openSupportPanelOverlay(...)`. Prefer
 * `router.pathname` in UI — after `input` that is already the base path.
 */
export const supportPanelOverlayRewrite: LocationRewrite = {
  // LocationRewrite mutates the provided URL in place by design.
  /* eslint-disable no-param-reassign */
  input: ({ url }) => {
    if (!url.pathname.startsWith('/support-panel/organizations')) {
      url.searchParams.delete(SUPPORT_PANEL_OVERLAY_SEARCH_PARAM);
      return url;
    }

    const suffix = matchOverlaySuffix(url.pathname);
    if (suffix) {
      url.pathname = url.pathname.slice(0, -suffix.length) || '/';
      url.searchParams.set(SUPPORT_PANEL_OVERLAY_SEARCH_PARAM, suffix.slice(1));
    } else {
      url.searchParams.delete(SUPPORT_PANEL_OVERLAY_SEARCH_PARAM);
    }

    return url;
  },
  output: ({ url }) => {
    const overlay = url.searchParams.get(SUPPORT_PANEL_OVERLAY_SEARCH_PARAM);
    if (overlay && url.pathname.startsWith('/support-panel/organizations')) {
      url.searchParams.delete(SUPPORT_PANEL_OVERLAY_SEARCH_PARAM);
      if (!url.pathname.endsWith(`/${overlay}`)) {
        url.pathname = `${url.pathname.replace(/\/$/, '')}/${overlay}`;
      }
    }
    return url;
  },
  /* eslint-enable no-param-reassign */
};
